import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { FaTimes, FaSearch, FaChevronUp, FaPlus, FaCheck } from 'react-icons/fa';
import { Ripple } from 'react-ripple-click';
import Select from "../../utils/Select.jsx";
import { components as rsComponents } from 'react-select';
import s from './StrategyModal.module.scss';
import './eaTheme.scss';
import { eaApi, eaErrorMessage, eaNotifyChanged } from './eaApi.js';
import {
  TIMEFRAMES, PRICE_FIELDS, tfDisplay, indicatorLabel, operandLabel,
  localizedOps, aliasForIndicator, newCondition, toServerCondition, fromServerRule,
  plainWords, warmupSummary, TF_MINUTES, validateIndicatorParams,
} from './eaModel.js';
import StrategySltpFields from './StrategySltpFields.jsx';
import { useTranslation } from 'react-i18next';
import { useSymbolContext } from '../../../contexts/Symbol-Context.js';
import TabIndicator from '../../utils/TabIndicator.jsx';
import { useChartContext } from '../../../contexts/Chart-Context.js';
import { dropdownOptionStyle } from '../../../utils/dropdownOptionStyles.js';

function EaCheck({ id, checked, onChange, label }) {
  return (
    <>
      <input
        className={s.checkboxInput}
        type="checkbox"
        id={id}
        checked={checked}
        onChange={onChange}
      />
      <label
        htmlFor={id}
        className={`my-chk-xbx ${s.eaChk}`}
        role={checked ? 'checked' : 'unchecked'}
      >
        <div className="chkx-xbx" />
        <span className="col-white">{label}</span>
      </label>
    </>
  );
}

function Section({ title, right, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const sectionRef = useRef(null);
  useEffect(() => {
    const node = sectionRef.current;
    const expand = () => setOpen(true);
    node?.addEventListener("strategy-expand", expand);
    return () => node?.removeEventListener("strategy-expand", expand);
  }, []);
  return (
    <section ref={sectionRef} className={`${s.section}${open ? ` ${s.sectionOpen}` : ''}`}>
      <header className={s.sectionHead}>
        <button
          type="button"
          className={s.sectionTitle}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <span className={`${s.chevron} ${open ? '' : s.chevronClosed}`}><FaChevronUp /></span>
          {title}
        </button>
        {right ? <div className={s.sectionRight}>{right}</div> : null}
      </header>
      <div className={s.sectionBody} hidden={!open}>{children}</div>
    </section>
  );
}

const KIND_OPTS = [
  { value: 'ref', label: 'indicator' },
  { value: 'value', label: 'value' },
];
const ACTION_OPTS = [
  { value: 'BUY', label: 'Buy' },
  { value: 'SELL', label: 'Sell' },
];

function isScrollInsideDropdownMenu(event) {
  const target = event.target;
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest('.strategy-tf__menu-list') ||
    target.closest('.strategy-tf__menu') ||
    target.closest('.strategy-symbol__menu-list') ||
    target.closest('.strategy-symbol__menu')
  );
}

function shouldCloseDropdownOnScroll(event) {
  // Keep scrolling inside the open menu list; collapse on any other scroll.
  return !isScrollInsideDropdownMenu(event);
}

function CompactSelect({
  options,
  value,
  onChange,
  className,
  styles,
  isDisabled,
}) {
  const selected = options.find((o) => String(o.value) === String(value)) ?? options[0] ?? null;
  return (
    <Select
      className={className}
      classNamePrefix="strategy-tf"
      options={options}
      value={selected}
      onChange={(opt) => { if (opt) onChange(opt.value); }}
      styles={styles}
      isSearchable={false}
      isDisabled={!!isDisabled}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      menuPlacement="auto"
      menuShouldScrollIntoView={false}
      closeMenuOnScroll={shouldCloseDropdownOnScroll}
    />
  );
}

function ConditionRow({
  cond, idx, operands, picked, onChange, onRemove, canRemove, selectStyles, disabled, kindOpts = KIND_OPTS, opOptions,
  invalidRight = false, errorField,
}) {
  const { t } = useTranslation();
  const operandOpts = operands.map((o) => ({ value: o, label: operandLabel(o, picked, t) }));
  return (
    <div className={s.condRow}>
      <CompactSelect
        className={s.dd}
        options={operandOpts}
        value={cond.left}
        onChange={(v) => onChange(idx, { left: v })}
        styles={selectStyles}
        isDisabled={disabled}
      />
      <CompactSelect
        className={s.dd}
        options={opOptions || []}
        value={cond.op}
        onChange={(v) => onChange(idx, { op: v })}
        styles={selectStyles}
        isDisabled={disabled}
      />
      <CompactSelect
        className={s.ddNarrow}
        options={kindOpts}
        value={cond.rightKind}
        onChange={(v) => onChange(idx, { rightKind: v })}
        styles={selectStyles}
        isDisabled={disabled}
      />
      {cond.rightKind === 'value' ? (
        <input
          className={`${s.numInput}${invalidRight ? ` ${s.numInputInvalid}` : ''}`}
          type="number"
          step="any"
          value={cond.rightValue}
          onChange={(e) => onChange(idx, { rightValue: e.target.value })}
          aria-invalid={invalidRight || undefined}
          data-strategy-error={invalidRight ? errorField : undefined}
        />
      ) : (
        <CompactSelect
          className={s.dd}
          options={operandOpts}
          value={cond.rightRef}
          onChange={(v) => onChange(idx, { rightRef: v })}
          styles={selectStyles}
          isDisabled={disabled}
        />
      )}
      <button
        type="button"
        className={`${s.rowX} close-icon`}
        onClick={() => onRemove(idx)}
        disabled={!canRemove}
        aria-label={t('ea.removeCondition')}
      >
        <FaTimes />
      </button>
    </div>
  );
}

function RuleEditor({
  conds, setConds, logic, setLogic, operands, picked, selectStyles, disabled, kindOpts, opOptions,
  invalidIndex = -1, invalidIssue, errorField,
}) {
  const { t } = useTranslation();
  const change = (idx, patch) => setConds((list) => list.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  const remove = (idx) => setConds((list) => list.filter((_, i) => i !== idx));
  const add = (nextLogic) => {
    setLogic(nextLogic);
    setConds((list) => [...list, newCondition(operands)]);
  };
  const logicLabel = logic === 'OR' ? t('ea.or').toUpperCase() : t('ea.and').toUpperCase();

  return (
    <>
      {conds.map((c, i) => (
        <div key={i}>
          {i > 0 && <div className={s.logicSep}>{logicLabel}</div>}
          <ConditionRow
            cond={c}
            idx={i}
            operands={operands}
            picked={picked}
            onChange={change}
            onRemove={remove}
            canRemove={conds.length > 1}
            selectStyles={selectStyles}
            disabled={disabled}
            kindOpts={kindOpts}
            opOptions={opOptions}
            invalidRight={invalidIndex === i && invalidIssue === 'rightValue'}
            errorField={errorField}
          />
        </div>
      ))}
      {/* AND / OR buttons sit below all conditions — matching reference design */}
      <div className={s.addLinks}>
        <button type="button" className={s.addLink} onClick={() => add('AND')}>{t('ea.and').toUpperCase()}</button>
        <button type="button" className={s.addLink} onClick={() => add('OR')}>{t('ea.or').toUpperCase()}</button>
      </div>
    </>
  );
}

function buildTfSelectStyle(buyColor) {
  return {
    control: (provided, state) => ({
      ...provided,
      minHeight: '24px',
      height: '24px',
      width: '100%',
      margin: 0,
      borderColor: (state.isFocused || state.menuIsOpen) ? `${buyColor} !important` : 'var(--surface-border, #3a4156) !important',
      backgroundColor: 'var(--surface-panel, #252b42) !important',
      borderRadius: '4px',
      boxShadow: 'none',
      cursor: 'pointer',
      alignItems: 'center',
      boxSizing: 'border-box',
      '&:hover': { borderColor: (state.isFocused || state.menuIsOpen) ? `${buyColor} !important` : 'var(--content-muted, #69758f) !important', boxShadow: 'none' },
    }),
    singleValue: (provided) => ({
      ...provided,
      paddingLeft: '0',
      margin: '0',
      color: 'var(--content-secondary, #b8bcc8) !important',
      fontSize: '11px',
      lineHeight: '1',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 2px 0 6px',
      height: '22px',
      display: 'flex',
      alignItems: 'center',
    }),
    input: (provided) => ({ ...provided, margin: 0, padding: 0 }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: '22px',
      alignSelf: 'center',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      padding: '0 4px',
      color: state.isFocused || state.selectProps.menuIsOpen
        ? `${buyColor} !important`
        : 'var(--content-secondary, #b8bcc8) !important',
      transition: 'color 0.15s ease',
      transform: 'none',
      '&:hover': { color: `${buyColor} !important` },
      svg: {
        width: '12px',
        height: '12px',
        transition: 'transform 0.2s ease',
        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        transformOrigin: 'center',
      },
    }),
    option: (provided, state) => dropdownOptionStyle(provided, state, {
      padding: '4px 8px',
      minHeight: '22px',
      fontSize: '11px',
      lineHeight: '1.3',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'var(--surface-panel, #252b42) !important',
      margin: '2px 0 0 0 !important',
      zIndex: 1000001,
      overflow: 'hidden',
      borderRadius: '4px',
      border: '1px solid var(--surface-border, #3a4156)',
      boxShadow: 'none',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '154px',
      minHeight: '3px',
      padding: '2px 0',
    }),
    menuPortal: (provided) => ({ ...provided, zIndex: 1000001 }),
  };
}

function SymbolMenuList(props) {
  const { selectProps } = props;
  const query = selectProps.symbolQuery || '';
  const setQuery = selectProps.setSymbolQuery;
  const placeholder = selectProps.symbolSearchPlaceholder || 'Search';
  const inputRef = useRef(null);
  useEffect(() => {
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, []);
  return (
    <div>
      <div
        className={s.symbolMenuSearch}
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      >
        <FaSearch className={s.symbolMenuSearchIcon} aria-hidden="true" />
        <input
          ref={inputRef}
          className={s.symbolMenuSearchInput}
          value={query}
          placeholder={placeholder}
          aria-label={placeholder}
          onChange={(e) => setQuery?.(e.target.value)}
          onMouseDown={(e) => { e.stopPropagation(); }}
          onKeyDown={(e) => {
            // Keep Backspace/Delete/arrows inside this field — react-select otherwise eats them.
            e.stopPropagation();
            if (typeof e.nativeEvent?.stopImmediatePropagation === 'function') {
              e.nativeEvent.stopImmediatePropagation();
            }
            if (e.key === 'Escape') {
              selectProps.onMenuClose?.();
            }
          }}
          onKeyUp={(e) => e.stopPropagation()}
        />
      </div>
      <rsComponents.MenuList {...props} />
    </div>
  );
}

function buildSymbolSelectStyle(buyColor) {
  return {
    control: (provided, state) => ({
      ...provided,
      minHeight: '24px',
      height: '24px',
      width: '100%',
      margin: 0,
      borderColor: (state.isFocused || state.menuIsOpen)
        ? `${buyColor} !important`
        : 'var(--surface-border, #3a4156) !important',
      backgroundColor: 'var(--surface-panel, #252b42) !important',
      borderRadius: '4px',
      boxShadow: 'none',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      alignItems: 'center',
      boxSizing: 'border-box',
      opacity: 1,
      '&:hover': {
        borderColor: state.isDisabled
          ? 'var(--surface-border, #3a4156) !important'
          : (state.isFocused || state.menuIsOpen)
            ? `${buyColor} !important`
            : 'var(--content-muted, #69758f) !important',
        boxShadow: 'none',
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
      color: 'var(--content-secondary, #b8bcc8) !important',
      fontSize: '11px',
      lineHeight: '1.2',
      fontWeight: 400,
    }),
    placeholder: (provided) => ({
      ...provided,
      margin: 0,
      color: 'var(--content-muted, #8b92b0) !important',
      fontSize: '11px',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 4px 0 8px',
      height: '22px',
      display: 'flex',
      alignItems: 'center',
    }),
    input: () => ({
      display: 'none',
      margin: 0,
      padding: 0,
      width: 0,
      maxWidth: 0,
      caretColor: 'transparent',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: '22px',
      alignSelf: 'center',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      padding: '0 6px',
      color: state.isFocused || state.selectProps.menuIsOpen
        ? `${buyColor} !important`
        : 'var(--content-secondary, #b8bcc8) !important',
      '&:hover': { color: `${buyColor} !important` },
      svg: {
        width: '12px',
        height: '12px',
        transition: 'transform 0.2s ease',
        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        transformOrigin: 'center',
      },
    }),
    option: (provided, state) => dropdownOptionStyle(provided, state, {
      padding: '6px 10px',
      minHeight: '28px',
      fontSize: '12px',
      lineHeight: '1.3',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'var(--surface-panel, #252b42) !important',
      margin: '4px 0 0 0 !important',
      zIndex: 1000001,
      overflow: 'hidden',
      borderRadius: '6px',
      border: '1px solid var(--surface-border, #3a4156)',
      boxShadow: '0 8px 20px rgba(0,0,0,.28)',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '140px',
      padding: '2px 0',
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      padding: '8px 10px',
      fontSize: '11px',
      color: 'var(--content-muted, #8b92b0)',
    }),
    menuPortal: (provided) => ({ ...provided, zIndex: 1000001 }),
  };
}

export default function StrategyModal({ open, onClose, userId, symbol, timeframe, editing, readOnly = false }) {
  const { t } = useTranslation();
  // Editing locks symbol + timeframe so the strategy cannot silently retarget.
  const lockInstrument = Boolean(editing) || readOnly;
  // An existing strategy keeps its own symbol. Taking the chart's current symbol here
  // would silently re-point the strategy at whatever the user happened to be viewing.
  const [targetSymbol, setTargetSymbol] = useState(editing?.symbol || symbol || '');
  const { symbolNames = [] } = useSymbolContext();
  const symbolOptions = useMemo(() => [...new Set([...symbolNames, targetSymbol].filter(Boolean))]
    .map(value => ({ value, label: value })), [symbolNames, targetSymbol]);
  const symbolSelectRef = useRef(null);
  const [symbolMenuOpen, setSymbolMenuOpen] = useState(false);
  const [symbolAttempted, setSymbolAttempted] = useState(false);
  const [symbolQuery, setSymbolQuery] = useState('');
  const filteredSymbolOptions = useMemo(() => {
    const q = symbolQuery.trim().toLowerCase();
    if (!q) return symbolOptions;
    return symbolOptions.filter((option) => String(option.label || option.value || '').toLowerCase().includes(q));
  }, [symbolOptions, symbolQuery]);
  const symbolSelectComponents = useMemo(() => ({ MenuList: SymbolMenuList }), []);

  const { selectedStyle } = useChartContext();
  const buyColor = selectedStyle?.buyColor || '#00d97e';
  const sellColor = selectedStyle?.sellColor || '#ff4757';
  const accentVars = {
    '--ea-accent': buyColor,
    '--buy-color': buyColor,
    '--sell-color': sellColor,
  };

  const actionOpts = useMemo(
    () => [
      { value: 'BUY', label: t('common.buy') },
      { value: 'SELL', label: t('common.sell') },
    ],
    [t],
  );
  const kindOpts = useMemo(
    () => [
      { value: 'ref', label: t('ea.indicatorKind') },
      { value: 'value', label: t('ea.valueKind') },
    ],
    [t],
  );
  const opOptions = useMemo(() => localizedOps(t), [t]);

  const tfOptions = useMemo(
    () => TIMEFRAMES.map((t) => ({ value: t, label: tfDisplay(t) })),
    [],
  );
  const tfSelectStyle = useMemo(() => buildTfSelectStyle(buyColor), [buyColor]);
  const symbolSelectStyle = useMemo(() => buildSymbolSelectStyle(buyColor), [buyColor]);
  const modalRef = useRef(null);
  const tabRef = useRef('builder');
  const fullHRef = useRef(0);
  const draggingRef = useRef(false);
  const dragOffRef = useRef({ x: 0, y: 0 });
  const [advH, setAdvH] = useState(null);
  const [pos, setPos] = useState(null);
  const [dragging, setDragging] = useState(false);

  const [settings, setSettings] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [tab, setTab] = useState('builder');
  tabRef.current = tab;

  const [name, setName] = useState('');
  const [tf, setTf] = useState(timeframe || '5m');
  const selectedTf = tfOptions.find((o) => o.value === tf) || tfOptions[0];
  const [quantity, setQuantity] = useState('1');
  const [picked, setPicked] = useState([]);
  const indicatorId = useRef(0);
  const [inlineVisible, setInlineVisible] = useState(false);
  /** alias -> { latest, source, status } from POST /ea/indicators/preview */
  const [previewByAlias, setPreviewByAlias] = useState({});
  const [search, setSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  const [entryAction, setEntryAction] = useState('BUY');
  const [entryLogic, setEntryLogic] = useState('AND');
  const [entryConds, setEntryConds] = useState([]);

  const [exitEnabled, setExitEnabled] = useState(false);
  const [exitLogic, setExitLogic] = useState('OR');
  const [exitConds, setExitConds] = useState([]);

  const [slOn, setSlOn] = useState(false);
  const [slPnl, setSlPnl] = useState('');
  const [legacySlPips, setLegacySlPips] = useState('');
  const [tpOn, setTpOn] = useState(false);
  const [tpPnl, setTpPnl] = useState('');
  const [legacyTpPips, setLegacyTpPips] = useState('');
  const [marginInfo, setMarginInfo] = useState({
    requiredMargin: 0,
    freeMargin: 0,
    tooLow: false,
    exceedsFree: false,
    minRequired: 10,
    sltpError: '',
  });

  const [saving, setSaving] = useState(false);

  const maxIndicators = settings?.max_indicators_per_strategy ?? 2;
  const aliases = useMemo(() => picked.map((p) => p.alias), [picked]);
  // Dropdown order: Price → indicator aliases (EMA(9), RSI(14)...) — matches EaPanel reference
  const operands = useMemo(() => [...PRICE_FIELDS, ...aliases], [aliases]);

  // Catalog and limits are static per session; load once when the modal first opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    Promise.all([eaApi.settings(), eaApi.indicators()])
      .then(([st, inds]) => {
        if (cancelled) return;
        setSettings(st);
        setCatalog(inds || []);
      })
      .catch((e) => toast.error(eaErrorMessage(e, t('ea.loadEaSettingsFailed'))));
    return () => { cancelled = true; };
  }, [open, t]);

  // Deep-verify: show latest computed value per picked indicator (live candles, synthetic fallback).
  useEffect(() => {
    if (!open || !picked.length) {
      setPreviewByAlias({});
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      Promise.all(
        picked.map(async (p) => {
          try {
            const data = await eaApi.previewIndicator({
              code: p.code,
              params: p.params || {},
              // Omit userId from body — JWT supplies req.userId; body userId trips
              // authenticateJWT cache gate when session cache is cold.
              symbol: targetSymbol,
              timeframe: tf,
              tail: 5,
            });
            return [
              p.alias,
              {
                latest: data?.latest,
                source: data?.source,
                status: data?.latest != null && Number.isFinite(Number(data.latest)) ? 'ok' : 'empty',
              },
            ];
          } catch {
            return [p.alias, { latest: null, source: null, status: 'error' }];
          }
        }),
      ).then((entries) => {
        if (cancelled) return;
        setPreviewByAlias(Object.fromEntries(entries));
      });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, picked, userId, targetSymbol, tf]);

  // Seed the form exactly once per open. `editing` is a parent-owned object, so
  // depending on it directly would re-seed on every parent render and wipe typing.
  const seeded = useRef(false);
  useEffect(() => {
    if (!open) { seeded.current = false; return; }
    if (seeded.current) return;
    seeded.current = true;
    setTargetSymbol(editing?.symbol || symbol || '');
    setSymbolMenuOpen(false);
    setSymbolAttempted(false);
    setTab('builder');
    setSearch('');
    setPickerOpen(false);

    if (editing) {
      const inds = (editing.indicators || []).map((i) => ({
        uiKey: ++indicatorId.current,
        code: i.code,
        alias: i.alias,
        params: { ...(i.params || {}) },
      }));
      const al = inds.map((i) => i.alias);
      setName(editing.name || '');
      setTf(editing.timeframe || timeframe || '5m');
      setQuantity(String(editing.quantity ?? editing.lot_size ?? '1'));
      setPicked(inds);
      setEntryAction(String(editing.entry_rule?.action || 'BUY').toUpperCase());
      setEntryLogic(editing.entry_rule?.logic || 'AND');
      setEntryConds(fromServerRule(editing.entry_rule, al));
      setExitEnabled(Number(editing.exit_rule_enabled) !== 0);
      setExitLogic(editing.exit_rule?.logic || 'OR');
      setExitConds(fromServerRule(editing.exit_rule, al));
      const hasSlPnl = Number(editing.stop_loss_pnl) > 0;
      const hasTpPnl = Number(editing.take_profit_pnl) > 0;
      const hasSlPips = Number(editing.stop_loss_pips) > 0;
      const hasTpPips = Number(editing.take_profit_pips) > 0;
      setSlOn(hasSlPnl || hasSlPips);
      setSlPnl(hasSlPnl ? String(editing.stop_loss_pnl) : '');
      setLegacySlPips(hasSlPips ? String(editing.stop_loss_pips) : '');
      setTpOn(hasTpPnl || hasTpPips);
      setTpPnl(hasTpPnl ? String(editing.take_profit_pnl) : '');
      setLegacyTpPips(hasTpPips ? String(editing.take_profit_pips) : '');
    } else {
      setName('');
      setTf(timeframe || '5m');
      setQuantity('1');
      setPicked([]);
      setEntryAction('BUY');
      setEntryLogic('AND');
      setEntryConds([newCondition([])]);
      setExitEnabled(false);
      setExitLogic('OR');
      setExitConds([]);
      setSlOn(false);
      setSlPnl('');
      setLegacySlPips('');
      setTpOn(false);
      setTpPnl('');
      setLegacyTpPips('');
    }
  }, [open, editing, timeframe, symbol]);

  // Conditions written against an indicator that was since removed would fail the
  // server's validator with an unhelpful message; repoint them as soon as it goes.
  useEffect(() => {
    if (!operands.length) return;
    const fix = (list) => list.map((c) => ({
      ...c,
      left: operands.includes(c.left) ? c.left : operands[0],
      rightRef: operands.includes(c.rightRef) ? c.rightRef : operands[operands.length - 1],
    }));
    setEntryConds((l) => fix(l));
    setExitConds((l) => fix(l));
  }, [operands.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (catalog || []).filter((ind) => {
      if (!q) return true;
      return [ind.name, ind.code, ind.description].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [catalog, search]);

  const addIndicator = (def) => {
    if (picked.length >= maxIndicators) {
      toast.info(t('ea.indicatorLimit', { max: maxIndicators }));
      return;
    }
    const params = { ...(def.default_params || {}) };
    setPicked((list) => [...list, { uiKey: ++indicatorId.current, code: def.code, alias: aliasForIndicator(def.code, params, list), params }]);
    setPickerOpen(false);
    setSearch('');
  };

  // Computed from the current `picked` rather than inside a setState updater: React may
  // invoke an updater twice, and the nested rule setters would then run twice as well.
  const patchIndicator = (idx, patch) => {
    const current = picked[idx];
    if (!current) return;
    const updated = { ...current, params: { ...current.params, ...patch } };
    const others = picked.filter((_, i) => i !== idx);
    // The alias encodes period/type, so it has to follow when either changes.
    const newAlias = aliasForIndicator(updated.code, updated.params, others);
    const oldAlias = current.alias;
    updated.alias = newAlias;

    setPicked(picked.map((p, i) => (i === idx ? updated : p)));

    if (newAlias !== oldAlias) {
      const swap = (c) => ({
        ...c,
        left: c.left === oldAlias ? newAlias : c.left,
        rightRef: c.rightRef === oldAlias ? newAlias : c.rightRef,
      });
      setEntryConds((l) => l.map(swap));
      setExitConds((l) => l.map(swap));
    }
  };

  const removeIndicator = (idx) => setPicked((list) => list.filter((_, i) => i !== idx));

  const warmup = useMemo(
    () => warmupSummary(picked, entryConds, exitEnabled ? exitConds : []),
    [picked, entryConds, exitConds, exitEnabled]
  );

  const validation = useMemo(() => {
    const fail = (field, message, extra = {}) => ({ field, message, ...extra });
    if (!targetSymbol) return fail('symbol', t('ea.selectSymbolFirst'));
    if (!name.trim()) return fail('name', t('ea.nameRequired'));
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty <= 0) return fail('quantity', t('ea.quantityGreaterThanZero'));
    if (!marginInfo.ready || marginInfo.symbol !== targetSymbol || marginInfo.quantity !== qty || marginInfo.direction !== entryAction) return fail('margin', 'Waiting for current margin calculation…');
    if (marginInfo?.tooLow) {
      return fail('margin', t('ea.marginGreaterThan', { min: marginInfo.minRequired ?? 10 }));
    }
    if (marginInfo?.exceedsFree) {
      return fail('margin', t('ea.marginExceedsFree'));
    }
    if (marginInfo?.sltpError) {
      return fail(marginInfo.slInvalid ? 'sl' : 'tp', marginInfo.sltpError);
    }
    for (const p of picked) {
      const def = catalog.find((d) => d.code === p.code);
      const paramErr = validateIndicatorParams(p, def);
      if (paramErr) return fail('indicators', paramErr);
    }
    // Server stores account PNL; UI still lets the user type pips/price/% and we convert.
    const sl = slOn ? Number(slPnl) : null;
    if (slOn && (!Number.isFinite(sl) || sl <= 0)) return fail('sl', t('ea.slNeedsPositivePnl'));
    if (tpOn) {
      const tp = Number(tpPnl);
      if (!Number.isFinite(tp) || tp <= 0) return fail('tp', t('ea.tpNeedsPositivePnl'));
    }
    const lists = [[t('ea.eventEntry'), entryConds, 'entry']];
    if (exitEnabled) lists.push([t('ea.eventExit'), exitConds, 'exit']);
    for (const [label, list, field] of lists) {
      if (!list.length) return fail(field, t('ea.ruleNeedsCondition', { label }));
      for (let i = 0; i < list.length; i += 1) {
        const c = list[i];
        if (!operands.includes(c.left)) return fail(field, t('ea.ruleRemovedIndicator', { label }), { condIndex: i, issue: 'left' });
        if (c.rightKind === 'value') {
          if (!String(c.rightValue).trim() || !Number.isFinite(Number(c.rightValue))) {
            return fail(field, t('ea.ruleNeedsNumber', { label }), { condIndex: i, issue: 'rightValue' });
          }
        } else if (!operands.includes(c.rightRef)) {
          return fail(field, t('ea.ruleRemovedIndicator', { label }), { condIndex: i, issue: 'rightRef' });
        }
      }
    }
    return null;
  }, [name, targetSymbol, picked, quantity, slOn, slPnl, tpOn, tpPnl, entryConds, exitConds, exitEnabled, operands, marginInfo, catalog, entryAction, t]);

  const error = validation?.message;
  const errorField = validation?.field;
  const errorCondIndex = Number.isInteger(validation?.condIndex) ? validation.condIndex : -1;
  const errorIssue = validation?.issue;
  const fieldError = (field) => (
    <div data-strategy-error={errorCondIndex < 0 && validation?.field === field ? field : undefined} className={s.fieldError} role={validation?.field === field ? 'alert' : undefined}>
      {validation?.field === field ? error : null}
    </div>
  );

  useEffect(() => {
    if (!open || !errorField) { setInlineVisible(false); return; }
    const node = modalRef.current?.querySelector(`[data-strategy-error="${errorField}"]`);
    const root = modalRef.current?.querySelector('#ea-strategy-modal-body');
    if (!node || !root) { setInlineVisible(false); return; }
    const Observer = node.ownerDocument.defaultView.IntersectionObserver;
    if (!Observer) return;
    const observer = new Observer(([entry]) => setInlineVisible(entry.isIntersecting && entry.intersectionRatio > 0.9), { root, threshold: [0, 0.9, 1] });
    observer.observe(node);
    return () => observer.disconnect();
  }, [open, errorField, error, tab]);

  const showError = () => {
    setTab('builder');
    if (validation?.field === 'symbol') {
      setSymbolAttempted(true);
      setSymbolMenuOpen(true);
      requestAnimationFrame(() => symbolSelectRef.current?.focus());
    }
    const node = modalRef.current?.querySelector(`[data-strategy-error="${validation?.field}"]`);
    const section = node?.closest('section');
    if (section) section.dispatchEvent(new (section.ownerDocument.defaultView.Event)('strategy-expand'));
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (node) {
        const body = modalRef.current?.querySelector('#ea-strategy-modal-body');
        if (body) {
          const bodyRect = body.getBoundingClientRect();
          const nodeRect = node.getBoundingClientRect();
          body.scrollBy({ top: nodeRect.top - bodyRect.top - (bodyRect.height / 2) + (nodeRect.height / 2), behavior: 'smooth' });
        } else {
          node.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }
      if (node?.matches?.('input, button, select, textarea')) {
        node.focus({ preventScroll: true });
        return;
      }
      const container = node?.closest('[data-risk], label, .condRow') || section;
      (container?.querySelector('input:not(:disabled)') || container?.querySelector('button:not(:disabled)'))?.focus({ preventScroll: true });
    }));
  };

  const payload = useMemo(() => ({
    userId,
    name: name.trim(),
    symbol: targetSymbol,
    timeframe: tf,
    quantity: Number(quantity),
    stop_loss_pnl: slOn ? Number(slPnl) || null : null,
    take_profit_pnl: tpOn ? Number(tpPnl) || null : null,
    // Clear legacy pip columns when saving from the new UI.
    stop_loss_pips: null,
    take_profit_pips: null,
    exit_rule_enabled: exitEnabled ? 1 : 0,
    indicators: picked.map((p) => ({ code: p.code, alias: p.alias, params: p.params })),
    entry_rule: { logic: entryLogic, action: entryAction, conditions: entryConds.map(toServerCondition) },
    exit_rule: { logic: exitLogic, action: entryAction, conditions: exitConds.map(toServerCondition) },
  }), [userId, name, targetSymbol, tf, quantity, slOn, slPnl, tpOn, tpPnl, exitEnabled, picked,
       entryLogic, entryAction, entryConds, exitLogic, exitConds]);

  const summary = useMemo(() => plainWords({
    tf, picked, entryConds, entryLogic, entryAction, exitConds, exitLogic,
    exitEnabled, lotSize: quantity,
    slOn, stopLossPnl: slPnl,
    tpOn, takeProfitPnl: tpPnl,
  }, t), [tf, picked, entryConds, entryLogic, entryAction, exitConds, exitLogic,
       exitEnabled, quantity, slOn, slPnl, tpOn, tpPnl, t]);

  const save = async () => {
    if (saving) return;
    if (error) { showError(); return; }
    setSaving(true);
    try {
      if (editing?.id) {
        await eaApi.update(editing.id, payload);
        toast.success(t('ea.strategyUpdated'));
      } else {
        await eaApi.create(payload);
        toast.success(t('ea.strategyCreated'));
      }
      eaNotifyChanged();
      onClose();
    } catch (e) {
      toast.error(eaErrorMessage(e, t('ea.couldNotSaveStrategy')));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!open) {
      fullHRef.current = 0;
      setAdvH(null);
      setPos(null);
      setDragging(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const el = modalRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (tabRef.current !== 'builder') return;
      const h = el.offsetHeight;
      if (h > fullHRef.current) fullHRef.current = h;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [open]);

  const switchTab = (next) => {
    if (next === 'advanced' && modalRef.current) {
      const h = modalRef.current.offsetHeight;
      const lock = Math.max(h, fullHRef.current || 0);
      fullHRef.current = lock;
      setAdvH(lock);
    }
    setTab(next);
  };

  const onDragStart = (e) => {
    if (e.target.closest('button, input, select, a')) return;
    if (e.button != null && e.button !== 0) return;
    const el = modalRef.current;
    if (!el) return;
    const isTouch = e.type === 'touchstart';
    const pt = isTouch ? e.touches[0] : e;
    const rect = el.getBoundingClientRect();
    dragOffRef.current = { x: pt.clientX - rect.left, y: pt.clientY - rect.top };
    draggingRef.current = true;
    setPos({ x: rect.left, y: rect.top });
    setDragging(true);
    e.preventDefault();
  };

  const handleDrag = useCallback((e) => {
    if (!draggingRef.current || !modalRef.current) return;
    const win = (() => { try { return window.top; } catch { return window; } })();
    const isTouch = e.type === 'touchmove';
    const pt = isTouch ? e.touches[0] : e;
    const { offsetWidth: w, offsetHeight: h } = modalRef.current;
    const x = Math.min(Math.max(pt.clientX - dragOffRef.current.x, 0), Math.max(0, win.innerWidth - w));
    const y = Math.min(Math.max(pt.clientY - dragOffRef.current.y, 0), Math.max(0, win.innerHeight - h));
    setPos({ x, y });
    if (isTouch) e.preventDefault();
  }, []);

  const handleDragEnd = useCallback(() => {
    draggingRef.current = false;
    setDragging(false);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const win = (() => { try { return window.top; } catch { return window; } })();
    const doc = win.document;
    doc.body.style.userSelect = 'none';
    win.addEventListener('mousemove', handleDrag);
    win.addEventListener('mouseup', handleDragEnd);
    win.addEventListener('touchmove', handleDrag, { passive: false });
    win.addEventListener('touchend', handleDragEnd);
    return () => {
      doc.body.style.userSelect = '';
      win.removeEventListener('mousemove', handleDrag);
      win.removeEventListener('mouseup', handleDragEnd);
      win.removeEventListener('touchmove', handleDrag);
      win.removeEventListener('touchend', handleDragEnd);
    };
  }, [dragging, handleDrag, handleDragEnd]);

  useEffect(() => {
    if (!open || !symbolMenuOpen || lockInstrument) return undefined;
    const body = document.getElementById('ea-strategy-modal-body');
    if (!body) return undefined;
    const onScroll = () => {
      setSymbolMenuOpen(false);
      setSymbolQuery('');
    };
    body.addEventListener('scroll', onScroll, { passive: true });
    return () => body.removeEventListener('scroll', onScroll);
  }, [open, symbolMenuOpen, lockInstrument]);

  useEffect(() => {
    if (!open) return;
    const win = (() => { try { return window.top; } catch { return window; } })();
    const onKey = (e) => { if (e.key === 'Escape' && !e.defaultPrevented) { if (pickerOpen) setPickerOpen(false); else onClose(); } };
    win.addEventListener('keydown', onKey);
    return () => win.removeEventListener('keydown', onKey);
  }, [open, onClose, pickerOpen]);

  useEffect(() => {
    if (!open) return;
    const modal = modalRef.current;
    if (!modal) return;
    const doc = modal.ownerDocument;
    const previous = doc.activeElement;
    const focusable = () => [...modal.querySelectorAll('button, input, select, textarea, [tabindex="0"]')]
      .filter(node => !node.disabled && node.getClientRects().length && !node.closest('[hidden], [aria-hidden="true"]'));
    const frame = doc.defaultView.requestAnimationFrame(() => {
      (modal.querySelector('input:not(:disabled)') || focusable()[0])?.focus({ preventScroll: true });
    });
    const trap = event => {
      if (event.key !== 'Tab') return;
      const nodes = focusable();
      if (!nodes.length) return;
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (event.shiftKey && (doc.activeElement === first || !modal.contains(doc.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (doc.activeElement === last || !modal.contains(doc.activeElement))) {
        event.preventDefault(); first.focus();
      }
    };
    doc.addEventListener('keydown', trap);
    return () => {
      doc.defaultView.cancelAnimationFrame(frame);
      doc.removeEventListener('keydown', trap);
      if (previous?.isConnected) previous.focus?.({ preventScroll: true });
    };
  }, [open]);

  // Sync --ea-accent into the top-level document so portal CSS vars resolve
  useEffect(() => {
    try {
      window.top.document.documentElement.style.setProperty('--ea-accent', buyColor);
    } catch { /* cross-origin guard */ }
  }, [buyColor]);

  if (!open) return null;

  const warmupHours = warmup.bars ? Math.round((warmup.bars * (TF_MINUTES[tf] || 5)) / 60) : 0;

  const body = (
    <div className={s.backdrop} style={accentVars} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={modalRef}
        className={[
          s.modal,
          readOnly ? s.modalReadOnly : '',
          pos ? s.modalMoved : '',
          dragging ? s.modalDragging : '',
          tab === 'advanced' && advH ? s.modalFixedH : '',
        ].filter(Boolean).join(' ')}
        style={{
          ...(pos ? { '--ea-modal-top': `${pos.y}px`, '--ea-modal-left': `${pos.x}px` } : {}),
          ...(tab === 'advanced' && advH ? { '--ea-modal-h': `${advH}px` } : {}),
        }}
        role="dialog"
        aria-modal="true"
      >

        <header
          className={s.head}
          onMouseDown={onDragStart}
          onTouchStart={onDragStart}
        >
          <div className={s.headText}>
            <div className={s.headTitle}>
              {readOnly ? t('ea.viewStrategy') : editing ? t('ea.editStrategy') : t('ea.buildStrategy')}
            </div>
            <div className={s.headSub}>{targetSymbol || t('ea.noSymbolSelected')} · {tfDisplay(tf)}</div>
          </div>
        </header>

        <div
          className={s.metaStrip}
          onMouseDown={onDragStart}
          onTouchStart={onDragStart}
        >
          <span className={s.chip}><b>{t('ea.indicators')}</b><span>{picked.length}/{maxIndicators}</span></span>
          <span className={`${s.chip} ${exitEnabled ? s.chipOn : s.chipOff}`}>
            <b>{t('ea.exitRule')}</b><span>{exitEnabled ? t('ea.on') : t('ea.off')}</span>
          </span>
          <span className={`${s.chip} ${slOn ? s.chipOn : s.chipOff}`}>
            <b>{t('ea.slPnl')}</b><span>{slOn ? (slPnl || '…') : t('ea.off')}</span>
          </span>
          <span className={`${s.chip} ${tpOn ? s.chipOn : s.chipOff}`}>
            <b>{t('ea.tpPnl')}</b><span>{tpOn ? (tpPnl || '…') : t('ea.off')}</span>
          </span>
        </div>

        <div className={s.tabs}>
          <TabIndicator activeKey={tab} />
          <button type="button" data-active={tab === 'builder'} className={tab === 'builder' ? s.tabActive : s.tab} onClick={() => switchTab('builder')}>
            {t('ea.builder')}
          </button>
          <button type="button" data-active={tab === 'advanced'} className={tab === 'advanced' ? s.tabActive : s.tab} onClick={() => switchTab('advanced')}>
            {t('ea.advancedIr')}
          </button>
        </div>

        <div id="ea-strategy-modal-body" className={s.body}>
          <div
            className={tab === 'builder' ? s.pane : s.paneHidden}
            aria-hidden={tab !== 'builder'}
          >
            <fieldset className={s.formLock} disabled={readOnly}>
            <>
              <div className={s.sectionList}>
              <Section title={t('ea.strategy')}>
                <div className={s.grid3}>
                  <label className={s.field}>
                    <span className={s.label}>{t('ea.name')}</span>
                    <input
                      className={`${s.input} ${s.inputPlain}`}
                      value={name}
                      placeholder={t('ea.namePlaceholder')}
                      aria-invalid={validation?.field === 'name'}
                      maxLength={60}
                      onChange={(e) => setName(e.target.value)}
                    />
                    {fieldError('name')}
                  </label>
                  <div className={s.field}>
                    <label className={s.label} htmlFor="strategy-symbol">{t('orderPanel.symbol', 'Symbol')}</label>
                    <Select ref={symbolSelectRef} inputId="strategy-symbol" options={filteredSymbolOptions}
                      value={symbolOptions.find(option => option.value === targetSymbol) || null}
                      placeholder={t('ea.selectSymbolFirst')} isSearchable={false} isDisabled={lockInstrument}
                      classNamePrefix="strategy-symbol" styles={symbolSelectStyle}
                      components={symbolSelectComponents}
                      symbolQuery={symbolQuery}
                      setSymbolQuery={setSymbolQuery}
                      symbolSearchPlaceholder={t('tables.searchSymbol', 'Search Symbol..')}
                      menuIsOpen={lockInstrument ? false : symbolMenuOpen}
                      onMenuOpen={() => {
                        if (lockInstrument) return;
                        setSymbolMenuOpen(true);
                        setSymbolQuery('');
                      }}
                      onMenuClose={() => {
                        // Defer close so focusing the in-menu search does not instantly dismiss the menu.
                        window.setTimeout(() => {
                          const active = document.activeElement;
                          if (active?.closest?.('.strategy-symbol__menu') || active?.closest?.('.strategy-symbol__menu-portal')) return;
                          setSymbolMenuOpen(false);
                          setSymbolQuery('');
                        }, 0);
                      }}
                      onChange={option => { setTargetSymbol(option?.value || ''); setSymbolMenuOpen(false); setSymbolQuery(''); }}
                      backspaceRemovesValue={false}
                      escapeClearsValue={false}
                      aria-invalid={symbolAttempted && !targetSymbol}
                      aria-describedby="strategy-symbol-error" maxMenuHeight={180}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition="fixed"
                      menuPlacement="auto"
                      menuShouldScrollIntoView={false}
                      blurInputOnSelect={false}
                      closeMenuOnSelect
                      closeMenuOnScroll={shouldCloseDropdownOnScroll}
                      noOptionsMessage={() => t('common.noResultsFound')}
                    />
                    <div id="strategy-symbol-error" data-strategy-error="symbol" className={s.fieldError} role={symbolAttempted && !targetSymbol ? 'alert' : undefined}>
                      {symbolAttempted && !targetSymbol ? t('ea.selectSymbolFirst') : null}
                    </div>
                  </div>
                  <div className={`${s.field} ${s.tfField}`}>
                    <span className={s.label}>{t('ea.timeframe')}</span>
                    <Select
                      className={s.tfSelect}
                      options={tfOptions}
                      value={selectedTf}
                      onChange={(opt) => { if (opt) setTf(opt.value); }}
                      styles={tfSelectStyle}
                      isSearchable={false}
                      isDisabled={lockInstrument}
                      classNamePrefix="strategy-tf"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition="fixed"
                      menuPlacement="auto"
                      menuShouldScrollIntoView={false}
                      closeMenuOnScroll={shouldCloseDropdownOnScroll}
                    />
                  </div>
                </div>
              </Section>

              <Section title={t('ea.indicators')} right={<span className={s.count}>{picked.length} {t('ea.of')} {maxIndicators}</span>}>
                {fieldError('indicators')}
                {picked.map((p, i) => {
                  const def = catalog.find((d) => d.code === p.code);
                  const schema = def?.param_schema && typeof def.param_schema === 'object' ? def.param_schema : {};
                  const schemaKeys = Object.keys(schema);
                  return (
                    <div key={p.uiKey} className={s.indRow}>
                      <span className={s.indName}>{def?.name || p.code}</span>
                      {schemaKeys.length === 0 ? (
                        <span className={s.pickerDesc}>{t('ea.noParameters')}</span>
                      ) : schemaKeys.map((key) => {
                        const rule = schema[key];
                        if (rule?.type === 'enum' && Array.isArray(rule.values)) {
                          return (
                            <CompactSelect
                              key={key}
                              className={s.ddNarrow}
                              options={rule.values.map((v) => ({
                                value: v,
                                label: t(`ea.enum.${String(v)}`, { defaultValue: String(v) }),
                              }))}
                              value={p.params[key] ?? rule.default ?? rule.values[0]}
                              onChange={(v) => patchIndicator(i, { [key]: v })}
                              styles={tfSelectStyle}
                              isDisabled={readOnly}
                            />
                          );
                        }
                        if (rule?.type === 'number') {
                          return (
                            <input
                              key={key}
                              className={`${s.numInput} ${s.inputPlain}`}
                              type="number"
                              title={key}
                              min={rule.min ?? 2}
                              max={rule.max ?? 500}
                              value={p.params[key] ?? rule.default ?? ''}
                              onChange={(e) => patchIndicator(i, { [key]: Number(e.target.value) })}
                              disabled={readOnly}
                            />
                          );
                        }
                        return null;
                      })}
                      {(() => {
                        const prev = previewByAlias[p.alias];
                        const latest = prev?.latest;
                        const fmt =
                          latest != null && Number.isFinite(Number(latest))
                            ? Number(latest).toFixed(Math.abs(Number(latest)) >= 100 ? 2 : 4)
                            : '—';
                        const title =
                          prev?.status === 'ok'
                            ? t('ea.indicatorPreviewTitle', { source: prev.source || '—' })
                            : prev?.status === 'error'
                              ? t('ea.indicatorPreviewFailed')
                              : t('ea.indicatorPreviewPending');
                        return (
                          <span className={s.indPreview} title={title}>
                            {fmt}
                          </span>
                        );
                      })()}
                      <code className={s.alias}>{p.alias}</code>
                      <button type="button" className={`${s.rowX} close-icon`} onClick={() => removeIndicator(i)} aria-label={t('ea.removeIndicator')}>
                        <FaTimes />
                      </button>
                    </div>
                  );
                })}

                {pickerOpen ? (
                  <div className={s.picker}>
                    <div className={s.pickerSearch}>
                      <FaSearch className={s.pickerSearchIcon} />
                      <input
                        autoFocus
                        type="text"
                        className={s.pickerSearchInput}
                        placeholder={t('ea.searchIndicators')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <button type="button" className={s.headerClose} onClick={() => setPickerOpen(false)} aria-label={t('ea.close')}><FaTimes /></button>
                    </div>
                    <div className={s.pickerList}>
                      {filteredCatalog.length === 0 && <div className={s.pickerEmpty}>{t('ea.noIndicatorMatches', { search })}</div>}
                      {filteredCatalog.map((ind) => (
                        <button
                          type="button"
                          key={ind.code}
                          className={s.pickerItem}
                          onClick={() => addIndicator(ind)}
                        >
                          <span className={s.pickerName}>{ind.name}</span>
                          {ind.description ? (
                            <span className={s.pickerDesc}>{ind.description}</span>
                          ) : null}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={s.addIndicator}
                    onClick={() => setPickerOpen(true)}
                    disabled={picked.length >= maxIndicators}
                  >
                    <FaPlus /> {t('ea.addIndicator')}
                  </button>
                )}
              </Section>

              <Section title={t('ea.entryRule')}>
                {fieldError('entry')}
                <RuleEditor
                  conds={entryConds}
                  setConds={setEntryConds}
                  logic={entryLogic}
                  setLogic={setEntryLogic}
                  operands={operands}
                  picked={picked}
                  selectStyles={tfSelectStyle}
                  disabled={readOnly}
                  kindOpts={kindOpts}
                  opOptions={opOptions}
                  invalidIndex={errorField === 'entry' ? errorCondIndex : -1}
                  invalidIssue={errorField === 'entry' ? errorIssue : undefined}
                  errorField="entry"
                />
                <div className={s.thenRow}>
                  <span className={s.then}>{t('ea.then')}</span>
                  <CompactSelect
                    className={s.ddNarrow}
                    options={actionOpts}
                    value={entryAction}
                    onChange={setEntryAction}
                    styles={tfSelectStyle}
                    isDisabled={readOnly}
                  />
                  <span className={s.then}>{t('ea.qty')}</span>
                  <input
                    className={s.numInput}
                    type="number"
                    step="1"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                  />
                </div>
                {fieldError('quantity')}
                <StrategySltpFields
                  marginError={fieldError('margin')}
                  slError={fieldError('sl')}
                  tpError={fieldError('tp')}
                  symbol={targetSymbol}
                  direction={entryAction}
                  quantity={quantity}
                  slOn={slOn}
                  tpOn={tpOn}
                  slPnl={slPnl}
                  tpPnl={tpPnl}
                  legacySlPips={legacySlPips}
                  legacyTpPips={legacyTpPips}
                  onSlPnlChange={setSlPnl}
                  onTpPnlChange={setTpPnl}
                  onMarginChange={setMarginInfo}
                  toggles={(
                    <div className={s.riskRow}>
                      <div className={s.riskCol}>
                        <EaCheck
                          id="ea-sl-on"
                          checked={slOn}
                          onChange={(e) => setSlOn(e.target.checked)}
                          label={t('ea.stopLoss')}
                        />
                      </div>
                      <div className={s.riskCol}>
                        <EaCheck
                          id="ea-tp-on"
                          checked={tpOn}
                          onChange={(e) => setTpOn(e.target.checked)}
                          label={t('ea.takeProfit')}
                        />
                      </div>
                    </div>
                  )}
                />
                {!slOn && !tpOn && (
                  <div className={s.hint}>
                    {exitEnabled
                      ? t('ea.noSlTpExitHint')
                      : t('ea.noSlTpHint')}
                  </div>
                )}
              </Section>

              <Section
                title={t('ea.exitRule')}
                right={
                  <EaCheck
                    id="ea-exit-on"
                    checked={exitEnabled}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setExitEnabled(on);
                      if (on && !exitConds.length) setExitConds([newCondition(operands)]);
                    }}
                    label={t('ea.enable')}
                  />
                }
              >
                {fieldError('exit')}
                {exitEnabled ? (
                  <RuleEditor
                    conds={exitConds}
                    setConds={setExitConds}
                    logic={exitLogic}
                    setLogic={setExitLogic}
                    operands={operands}
                    picked={picked}
                    selectStyles={tfSelectStyle}
                    disabled={readOnly}
                    kindOpts={kindOpts}
                    opOptions={opOptions}
                    invalidIndex={errorField === 'exit' ? errorCondIndex : -1}
                    invalidIssue={errorField === 'exit' ? errorIssue : undefined}
                    errorField="exit"
                  />
                ) : (
                  <div className={s.hint}>
                    {t('ea.exitRuleOffHint')}
                  </div>
                )}
              </Section>
              </div>

              <div className={s.summary}>
                <span className={s.summaryKey}>{t('ea.plainWords')}</span>
                <span>{summary}</span>
              </div>

              <div className={s.valid} hidden={!!error}>
                {error ? error : (
                  <>
                    <FaCheck /> {t('ea.validNeedsBars', { bars: warmup.bars })}
                    {warmupHours ? ` ${t('ea.aboutHours', { hours: warmupHours, tf: tfDisplay(tf) })}` : ''} · {t('ea.depth')} {warmup.lookback ? 1 : 0}
                  </>
                )}
              </div>
            </>
            </fieldset>
          </div>
          <div
            className={tab === 'advanced' ? s.pane : s.paneHidden}
            aria-hidden={tab !== 'advanced'}
          >
            <div className={s.irWrap}>
              <div className={s.irNote}>{t('ea.irNote')}</div>
              <pre className={s.ir}>{JSON.stringify(payload, null, 2)}</pre>
            </div>
          </div>
        </div>

        {error && !inlineVisible && <div className={s.footerError} role="alert">{error}</div>}
        <footer className={s.foot}>
          <div className={s.footBtns}>
            {readOnly ? (
              <button type="button" className={s.btnGhost} onClick={onClose}>
                <Ripple />
                {t('ea.close')}
              </button>
            ) : (
              <>
                <button type="button" className={s.btnGhost} onClick={onClose}>
                  <Ripple />
                  {t('common.cancel')}
                </button>
                <button type="button" className={`${s.btnPrimary} ${error ? s.btnInvalid : ''}`} aria-disabled={!!error || saving} disabled={saving} onClick={save}>
                  <Ripple />
                  {saving ? t('ea.saving') : editing ? t('ea.saveChanges') : t('ea.attachStrategy')}
                </button>
              </>
            )}
          </div>
        </footer>
      </div>
    </div>
  );

  // Portal to window.top so the modal always escapes the amCharts iframe
  // (EaChartControl mounts inside #chartcontrols which may live in an iframe).
  const portalRoot = (() => {
    try { return window.top.document.body; } catch { return document.body; }
  })();
  return createPortal(body, portalRoot);
}
